import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Checkbox,
  Snackbar,
  Alert,
  Box,
  Typography,
  Card,
  IconButton,
  Drawer,
  Collapse,
} from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowLeft, Sort, KeyboardArrowDown, Close } from '@mui/icons-material';
import { getRebalance, CancelOrder } from '../../Redux/Actions/transactionService';
import { useDispatch, useSelector } from 'react-redux';
import OrderDetailsModal from '../Orders/OrderDetailsModal';
import BatchOrderDetailsModal from '../Orders/BatchOrderDetailsModal';

const renderTransactionDetails = (transactions) => {
  if (!transactions?.length) return null;

  return (
    <Box sx={{ 
      borderRadius: '8px',
      overflow: 'hidden',
      border: '1px solid #1344EB',
      backgroundColor:'#f9f9f9',
      padding:'0 12px'
    }}>
      <Table size="small" sx={{ 
        '& .MuiTableCell-root': {
          borderBottom: '1px solid #E0E0E0',
          padding: '12px 16px',
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          color: '#0D0C3A',
          fontWeight: '500 !important',
          fontSize: '18px !important',
          padding: '12px 16px',
          fontFamily: 'inherit !important',
          fontStyle: 'inherit',
          lineHeight: '24px',
          letterSpacing: '0.15px',
          textAlign:'center',
         
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
          backgroundColor: 'white',
          color: '#787A99',

          fontSize: '16px',
          backgroundColor:'#f9f9f9',
        },
        '& .MuiTableRow-root:last-child .MuiTableCell-root': {
          borderBottom: '1px solid #E0E0E0'
        },
        '& .MuiTableHead-root .MuiTableCell-root:first-child': {
          textAlign:'left'
        },
        '& .MuiTableBody-root .MuiTableCell-root:first-child': {
          textAlign:'left',
          color: '#43436A'
        },
        '& .MuiTableBody-root .MuiTableCell-root:not(:first-child)': {
          textAlign:'center'
        }
      }}>
        <TableHead>
          <TableRow>
            <TableCell>Asset</TableCell>
            <TableCell>Market Price</TableCell>
            <TableCell>Limit Price</TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell>Value ($)</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((transaction) => (
            <TableRow key={transaction.id}>
              <TableCell>{transaction.asset?.symbol || '-'}</TableCell>
              <TableCell>{transaction.price ? Number(transaction.price).toFixed(2) : '-'}</TableCell>
              <TableCell>{transaction.price ? Number(transaction.price).toFixed(2) : '-'}</TableCell>
              <TableCell>{transaction.quantity != null ? Number(transaction.quantity)?.toFixed(3) : '-'}</TableCell>
              <TableCell>{transaction.usdtEquivalent ? Number(transaction.usdtEquivalent)?.toFixed(3) : '-'}</TableCell>
              <TableCell sx={{ textTransform: 'uppercase' }}>{transaction?.status || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default function OrderTable({ selectedOrders, setSelectedOrders }) {
  const dispatch = useDispatch();
  const { orders } = useSelector((state) => state.transaction);
  const [selectedTab, setSelectedTab] = useState('Requested');
  const [selectedStrategies, setSelectedStrategies] = useState(['All']);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const userInfoString = localStorage.getItem("userInfo");
  const userInfo = userInfoString && userInfoString !== "undefined" ? JSON.parse(userInfoString) : null;
  const role = userInfo?.portal || 'MANAGER';
  const entityId = localStorage.getItem('entId');

  // First filter for manager discretion orders only
  const managerDiscretionOrders = React.useMemo(() => {
    return orders.filter(order => order.isManagerDiscretion === true);
  }, [orders]);

  // Calculate status counts based on specific requirements - only for isManagerDiscretion orders
  const statusCounts = React.useMemo(() => ({
    // Manager discretion orders with PENDING status
    Requested: orders.filter(order => 
      order.isManagerDiscretion === true &&
      order.type === 'MANAGER_DISCRETION' &&
      order.status === 'PENDING'
    ).length,
    // Non-manager discretion orders with PENDING status
    Pending: orders.filter(order => 
      order.isManagerDiscretion === true &&
      order.type !== 'MANAGER_DISCRETION' &&
      order.status === 'PENDING'
    ).length,
    // Orders with COMPLETED/PARTIALLY_COMPLETED status
    Executed: orders.filter(order => 
      order.isManagerDiscretion === true &&
      ['PARTIALLY_COMPLETED', 'COMPLETED'].includes(order.status)
    ).length,
    // Any order type with FAILED/CANCELLED status
    'Failed/Canceled': orders.filter(order => 
      order.isManagerDiscretion === true &&
      ['FAILED', 'CANCELLED'].includes(order.status)
    ).length,
  }), [orders]);

  // Filter orders based on status and strategy with updated logic
  const filteredOrders = React.useMemo(() => {
   
    return orders.filter(order => {
      // Exclude REBALANCE actions
      if (order.action === 'REBALANCE') return false;

      // For all tabs, only show orders where isManagerDiscretion is true
      if (order.isManagerDiscretion !== true) return false;

      const statusMatch = (() => {
        switch (selectedTab) {
          case 'Requested':
            return order.status === 'PENDING' && order.type === 'MANAGER_DISCRETION';
          case 'Pending':
            return order.status === 'PENDING' && order.type !== 'MANAGER_DISCRETION';
          case 'Executed':
            return ['PARTIALLY_COMPLETED', 'COMPLETED'].includes(order.status);
          case 'Failed/Canceled':
            return ['FAILED', 'CANCELLED'].includes(order.status);
          default:
            return true;
        }
      })();

      const strategyMatch = 
        selectedStrategies.includes('All') || 
        selectedStrategies.includes(order.strategy?.name);

      return statusMatch && strategyMatch;
    });
  }, [orders, selectedTab, selectedStrategies]);

  const statusTabs = [
    { label: 'Requested', count: statusCounts.Requested },
    { label: 'Executed', count: statusCounts.Executed },
    { label: 'Failed/Canceled', count: statusCounts['Failed/Canceled'] },
  ];

  // Get unique strategies from manager discretion orders
  const strategies = React.useMemo(() => {
    const uniqueStrategies = [...new Set(managerDiscretionOrders
      .filter(order => order.strategy?.name)
      .map(order => order.strategy.name)
    )];
    return ['All', ...uniqueStrategies];
  }, [managerDiscretionOrders]);

  const handleSortClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = (value) => {
    if (value) {
      // Implement sorting logic here
    }
    setAnchorEl(null);
  };

  useEffect(() => {
    dispatch(getRebalance());
  }, [dispatch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (order) => {
    setSelectedOrder(order);
    setModalOpen(true);
  };

  const handleCancelOrder = (order) => {
    showBanner(`Cancelling order for ${order.strategy?.name}...`, 'info');
    
    dispatch(CancelOrder({ orderId: order.id }))
      .unwrap()
      .then((response) => {
        if (response && response.statusCode === 200) {
          showBanner(`Order for ${order.strategy?.name} cancelled successfully`, 'success');
          // Refresh the orders list after successful cancellation
          dispatch(getRebalance());
        } else {
          // Handle case where API doesn't return error but operation wasn't successful
          showBanner(`Failed to cancel order: ${response?.message || 'Unknown error'}`, 'error');
        }
      })
      .catch((error) => {
        console.error('Error cancelling order:', error);
        showBanner(`Failed to cancel order: ${error.message || 'Unknown error'}`, 'error');
      });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOrder(null);
  };

  const handleOpenBatchModal = (event) => {
    event.stopPropagation();
    setBatchModalOpen(true);
  };

  const handleCloseBatchModal = () => {
    setBatchModalOpen(false);
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders((prevSelected) => {
      if (prevSelected.includes(orderId)) {
        return prevSelected.filter(id => id !== orderId);
      } else {
        // Allow multiple selections
        return [...prevSelected, orderId];
      }
    });
  };

  const showBanner = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Handle multiple strategy selection
  const handleStrategyClick = (strategy) => {
    setSelectedStrategies(prev => {
      if (strategy === 'All') {
        return ['All'];
      }
      
      const newSelection = prev.includes(strategy)
        ? prev.filter(s => s !== strategy)
        : [...prev.filter(s => s !== 'All'), strategy];
      
      return newSelection.length === 0 ? ['All'] : newSelection;
    });
  };

  const handleAssetInfoClick = (order) => {
    setSelectedAssets(order.assets);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedAssets(null);
  };

  const handleExpandRow = (orderId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const strategyScrollRef = React.useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const handleScrollStrategies = (direction) => {
    if (strategyScrollRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      strategyScrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (strategyScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = strategyScrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const scrollContainer = strategyScrollRef.current;
    if (scrollContainer) {
      handleScroll(); // Check initial state
      scrollContainer.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [strategies]);

  return (
    <>
      {/* Combined Status Tabs and Strategy Filters Card */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        mb: 2
      }}>
        {/* Status Tabs Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 3,
          py: 1.5,
          borderBottom: '1px solid #E0E0E0'
        }}>
          <Box sx={{ display: 'flex', gap: 4 }}>
            {Object.entries(statusCounts).map(([status, count]) => (
              <Box
                key={status}
                onClick={() => setSelectedTab(status)}
                sx={{
                  cursor: 'pointer',
                  color: selectedTab === status ? '#0066FF' : '#666666',
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    right: 0,
                    height: 2,
                    bgcolor: selectedTab === status ? '#0066FF' : 'transparent'
                  }
                }}
              >
                <Typography variant="body2">
                  {`${status} (${count})`}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button
            endIcon={<KeyboardArrowDown />}
            startIcon={<Sort />}
            sx={{ 
              textTransform: 'none',
              color: '#666666'
            }}
          >
            Sort
          </Button>
        </Box>

        {/* Strategy Filters Section */}
        <Box sx={{ 
          position: 'relative',
          py: 1.5,
          px: 2,
        }}>
          {showLeftArrow && (
            <IconButton 
              onClick={() => handleScrollStrategies('left')}
              sx={{ 
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                minWidth: 40, 
                height: 40,
                bgcolor: 'white',
                border: '1px solid #E0E0E0',
                borderRadius: '6px',
                boxShadow: '4px 0 8px rgba(0, 0, 0, 0.05)',
                zIndex: 1,
                '&:hover': { 
                  bgcolor: '#F5F5F5',
                  borderColor: '#E0E0E0'
                }
              }}
            >
              <KeyboardArrowLeft />
            </IconButton>
          )}
          {/* Strategy buttons container with padding-right to avoid overlap */}
          <Box 
            ref={strategyScrollRef}
            sx={{ 
              display: 'flex', 
              gap: 1,
              overflowX: 'auto',
              alignItems: 'center',
              px: showLeftArrow ? 6 : 0,
              pr: showRightArrow ? 6 : 0,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {strategies.map((strategy) => (
              <Button
                key={strategy}
                onClick={() => handleStrategyClick(strategy)}
                sx={{
                  py: 1,
                  px: 2.5,
                  border: '1px solid #E0E0E0',
                  borderRadius: '6px',
                  bgcolor: selectedStrategies.includes(strategy) ? '#EEF2FF' : 'white',
                  color: selectedStrategies.includes(strategy) ? '#0066FF' : '#666666',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  minHeight: '40px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: selectedStrategies.includes(strategy) ? '#EEF2FF' : '#F5F5F5',
                    borderColor: selectedStrategies.includes(strategy) ? '#0066FF' : '#E0E0E0',
                  }
                }}
              >
                {strategy}
              </Button>
            ))}
          </Box>
          {showRightArrow && (
            <IconButton 
              onClick={() => handleScrollStrategies('right')}
              sx={{ 
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                minWidth: 40, 
                height: 40,
                bgcolor: 'white',
                border: '1px solid #E0E0E0',
                borderRadius: '6px',
                boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.05)',
                zIndex: 1,
                '&:hover': { 
                  bgcolor: '#F5F5F5',
                  borderColor: '#E0E0E0'
                }
              }}
            >
              <KeyboardArrowRight />
            </IconButton>
          )}
        </Box>
      </Card>

      {/* Main Content Card */}
      <Card sx={{ 
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        backgroundColor: '#FFFFFF',
      }}>
        {/* Execute Multiple Orders Button - Only show in Requested tab */}
        {selectedTab === 'Requested' && (
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={selectedOrders.length === 0}
              onClick={handleOpenBatchModal}
              sx={{ 
                textTransform: 'none',
                bgcolor: '#0066FF',
                '&:hover': { bgcolor: '#0052CC' }
              }}
            >
              Execute Orders
            </Button>
          </Box>
        )}

        {/* Table Section */}
        <TableContainer sx={{ 
          overflow: selectedTab === 'Requested' ? 'auto' : 'visible' 
        }}>
          <Table sx={{
            '& .MuiTableCell-root': {
              borderBottom: selectedTab === 'Requested' ? '1px solid rgba(224, 224, 224, 1)' : 'none',
              width: selectedTab === 'Requested' ? 'auto' : '14.28%' 
            },
            '& .MuiTableRow-root': {
              borderBottom: selectedTab === 'Requested' ? '1px solid rgba(224, 224, 224, 1)' : 'none'
            }
          }}>
            <TableHead>
              <TableRow>
                {selectedTab === 'Requested' && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                      indeterminate={selectedOrders.length > 0 && selectedOrders.length < filteredOrders.length}
                      onChange={() => {
                        if (selectedOrders.length === filteredOrders.length) {
                          setSelectedOrders([]);
                        } else {
                          setSelectedOrders(filteredOrders.map(order => order.id));
                        }
                      }}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ color: selectedTab === 'Requested' ? 'inherit' : '#666666' }}>Investor Email</TableCell>
                <TableCell sx={{ color: selectedTab === 'Requested' ? 'inherit' : '#666666' }}>Order Type</TableCell>
                <TableCell sx={{ color: selectedTab === 'Requested' ? 'inherit' : '#666666' }}>Strategy Name</TableCell>
                <TableCell sx={{ color: selectedTab === 'Requested' ? 'inherit' : '#666666' }}>Amount ($)</TableCell>
                <TableCell sx={{ color: selectedTab === 'Requested' ? 'inherit' : '#666666' }}>Date of Creation</TableCell>
                <TableCell sx={{ color: selectedTab === 'Requested' ? 'inherit' : '#666666' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order) => (
                  <React.Fragment key={order.id}>
                    <TableRow>
                      {selectedTab === 'Requested' && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedOrders.includes(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{order.userRole?.user?.email}</TableCell>
                      <TableCell>{order.type}</TableCell>
                      <TableCell>{order.strategy?.name}</TableCell>
                      <TableCell>{order.amount?.toFixed(2)}</TableCell>
                      <TableCell>{new Date(order.createdAt).toLocaleString()}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {(selectedTab === 'Pending' || selectedTab === 'Executed') && order.transactions?.length > 0 && (
                            <Button
                              sx={{
                                textTransform: 'none',
                                color: '#2196F3',
                                fontWeight: 'bold',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => handleExpandRow(order.id)}
                            >
                              {expandedRows.has(order.id) ? 'Hide Asset Info' : 'See Asset Info'}
                            </Button>
                          )}
                          {order.status === 'PENDING' && (
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() => handleCancelOrder(order)}
                              size="small"
                            >
                              Cancel
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {order.transactions?.length > 0 && selectedTab !== 'Requested' && (
                      <TableRow>
                        <TableCell 
                          colSpan={6} 
                          sx={{ 
                            py: 0,
                            borderBottom: expandedRows.has(order.id) ? '1px solid #E0E0E0' : 'none',
                          }}
                        >
                          <Collapse in={expandedRows.has(order.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ 
                              py: 2,
                              px: 1,
                              overflow: 'visible',
                              backgroundColor: '#ffffff'
                            }}>
                              {renderTransactionDetails(order.transactions)} 
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderTop: '1px solid rgba(224, 224, 224, 1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Rows per page:
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              style={{ 
                marginLeft: '8px', 
                padding: '4px',
                border: '1px solid #E0E0E0',
                borderRadius: '4px'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              <KeyboardArrowLeft />
            </IconButton>
            <Box sx={{ mx: 2 }}>
              {page + 1}
            </Box>
            <IconButton
              disabled={page >= Math.ceil(filteredOrders.length / rowsPerPage) - 1}
              onClick={() => setPage(page + 1)}
            >
              <KeyboardArrowRight />
            </IconButton>
          </Box>
        </Box>
      </Card>

      {/* Modals and Snackbar */}
      <OrderDetailsModal open={modalOpen} handleClose={handleCloseModal} order={selectedOrder} showBanner={showBanner} />
      <BatchOrderDetailsModal
        open={batchModalOpen}
        handleClose={handleCloseBatchModal}
        orders={orders.filter(order => selectedOrders.includes(order.id))}
        showBanner={showBanner}
      />
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '600px',
            padding: '24px',
          },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Transaction Assets</Typography>
            <IconButton onClick={handleDrawerClose}>
              <Close />
            </IconButton>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Market Price</TableCell>
                  <TableCell>Limit Price</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Value ($)</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedAssets?.map((asset, index) => (
                  <TableRow key={index}>
                    <TableCell>{asset.symbol}</TableCell>
                    <TableCell>{asset.marketPrice}</TableCell>
                    <TableCell>{asset.limitPrice}</TableCell>
                    <TableCell>{asset.quantity}</TableCell>
                    <TableCell>{asset.value}</TableCell>
                    <TableCell>{asset.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Drawer>
    </>
  );
}

